import { useMemo } from "react";
import { useDebounce } from "usehooks-ts";

import { getInitialsFromFullName } from "../constants/constants";
import { useAppSelector } from "../redux/redux";
import { getLinkedFirm } from "../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../slices/ContactsSlice";
import { useGetStatusesQuery } from "../slices/StatusesSlice";
import { useGetUsersQuery } from "../slices/UserSlice";
import { useGetSearchWorksQuery } from "../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../slices/WorkTypeSlice";
import { SelectOptionType } from "../types/types";

type Params = {
  searchClients?: string;
  searchWorks?: string;
  includeUsers?: boolean;
  includeWorksSearch?: boolean;
};

export const useWorkFilterOptions = ({
  searchClients = "",
  searchWorks = "",
  includeUsers = false,
  includeWorksSearch = false,
}: Params) => {
  const user = useAppSelector((state) => state.appReducer.user);

  const orgId = getLinkedFirm()?.orgId || "";
  const debouncedSearchClient = useDebounce(searchClients, 50);
  const debouncedSearchWork = useDebounce(searchWorks, 300);

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: debouncedSearchClient || "ab",
      orgId,
      userId: user.id,
      isActive: true,
    });

  const { data: works, isFetching: isLoadingWorks } = useGetSearchWorksQuery(
    {
      query: debouncedSearchWork,
      orgId,
    },
    {
      skip: !includeWorksSearch,
    },
  );

  const { data: users, isFetching: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !includeUsers,
  });

  const { data: statuses, isFetching: isLoadingStatuses } = useGetStatusesQuery(
    {
      orgId,
    },
  );

  const { data: workTypes, isFetching: isLoadingWorkTypes } =
    useGetWorkTypesQuery({ orgId });

  const clientOptions = useMemo<
    (SelectOptionType & { email?: string; phone?: string })[]
  >(() => {
    return (
      contacts?.map((contact) => ({
        value: contact?.customerId || "",
        label: `${contact.customerName}`,
        avatar: getInitialsFromFullName(contact?.customerName),
        avatarColor: contact.businessContactId ? "purple" : "blue",
        // Work page client filter tells same-name clients apart (QA batch 16).
        email: contact.emailAddress || contact.email || undefined,
        phone: contact.phoneNumber || undefined,
      })) || []
    );
  }, [contacts]);

  const workOptions = useMemo<SelectOptionType[]>(() => {
    return works || [];
  }, [works]);

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((item) => ({
        avatar: getInitialsFromFullName(
          `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        ),
        avatarCustomColor: item?.userProfile?.color,
        label: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        value: `${item?.id}`,
      })) || []
    );
  }, [users]);

  const statusesOptions = useMemo<SelectOptionType[]>(() => {
    return (
      statuses?.flatMap((item) => [
        {
          label: item?.name,
          value: item?.id,
          color: item?.color,
          isBold: true,
        },
        ...((item?.workTypeSubStatuses || []).map((subItem) => ({
          label: subItem?.subStatus,
          value: subItem?.id,
          color: item?.color,
        })) as SelectOptionType[]),
      ]) || []
    );
  }, [statuses]);

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (
      workTypes?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || []
    );
  }, [workTypes]);

  return {
    clientOptions,
    workOptions,
    usersOptions,
    statusesOptions,
    workTypeOptions,

    isLoadingContacts,
    isLoadingWorks,
    isLoadingUsers,
    isLoadingStatuses,
    isLoadingWorkTypes,
  };
};
